import { makeObservable, observable, action, reaction, computed } from 'mobx';
import { Disposer } from '../../custom_shared/misc';
import { LayoutSettingsFactory } from '../factories';
import { InstanceUtils } from '../misc';
import { LayoutSettingsBaseModel } from '../models';
import SessionStore from './SessionStore';
import type ContractDraftHistory from '../history/ContractDraftHistory';

export default class SettingsStore extends Disposer {
    modalVisible: boolean = false;

    layoutSettings: LayoutSettingsBaseModel | null = null;

    layoutSettingsOptions: LayoutSettingsBaseModel[] = [];

    changedLayoutType: string | null = null;

    constructor(
        private readonly sessionStore: SessionStore,
        private readonly history: ContractDraftHistory
    ) {
        super();

        this.reactions.push(
            reaction(
                () => this.sessionStore.response,
                response => {
                    if (!response) {
                        return;
                    }

                    if (!this.history.hasPendingStructure) {
                        this.setLayoutSettings(LayoutSettingsFactory.createSettings(response.layoutSettings));
                    }
                    this.setLayoutSettingsOptions(
                        response.layoutSettingsOptions.map(LayoutSettingsFactory.createSettings)
                    );
                }
            )
        );

        makeObservable(this, {
            modalVisible: observable,
            layoutSettings: observable,
            layoutSettingsOptions: observable,
            changedLayoutType: observable,
            setModalVisible: action,
            setLayoutSettings: action,
            setLayoutSettingsOptions: action,
            setChangedLayoutType: action,
            selectedLayoutSettings: computed,
            isBasicLayoutSettings: computed,
            isLayersLayoutSettings: computed,
            settingsHasLayers: computed
        });
    }

    setModalVisible(modalVisible: boolean) {
        this.modalVisible = modalVisible;
    }

    setDraftLayoutSettings(layoutSettings: LayoutSettingsBaseModel, history: ContractDraftHistory) {
        const previous = this.layoutSettings;
        if (
            previous &&
            InstanceUtils.hasLayers(previous) &&
            InstanceUtils.hasLayers(layoutSettings) &&
            previous.numberOfLayers !== layoutSettings.numberOfLayers
        ) {
            history.capture({ itemId: 'NumberOfLayers' });
        }
        if (
            InstanceUtils.isSectionsLayoutSettings(previous) &&
            InstanceUtils.isSectionsLayoutSettings(layoutSettings) &&
            previous.numberOfSections !== layoutSettings.numberOfSections
        ) {
            history.capture({ itemId: 'NumberOfSections' });
        }
        if (
            InstanceUtils.isLayersWithSectionsLayoutSettings(previous) &&
            InstanceUtils.isLayersWithSectionsLayoutSettings(layoutSettings)
        ) {
            layoutSettings.layersSettings.forEach(layer => {
                const oldLayer = previous.layersSettings.find(old => old.layerId === layer.layerId);
                if (oldLayer && oldLayer.numberOfSections !== layer.numberOfSections) {
                    history.capture({ itemId: 'NumberOfSections', layerId: layer.layerId });
                }
            });
        }
        this.setLayoutSettings(layoutSettings);
    }

    setLayoutSettings(layoutSettings: LayoutSettingsBaseModel) {
        this.layoutSettings = layoutSettings;
    }

    setLayoutSettingsOptions(layoutSettingsOptions: LayoutSettingsBaseModel[]) {
        this.layoutSettingsOptions = layoutSettingsOptions;
    }

    setChangedLayoutType(changedLayoutType: string | null) {
        this.changedLayoutType = changedLayoutType;
    }

    get selectedLayoutSettings() {
        if (this.changedLayoutType) {
            return this.layoutSettingsOptions.find(settings => settings.type === this.changedLayoutType) ?? null;
        }

        return this.layoutSettings;
    }

    get isBasicLayoutSettings() {
        return InstanceUtils.isBasicLayoutSettings(this.layoutSettings);
    }

    get isLayersLayoutSettings() {
        return InstanceUtils.isLayersLayoutSettings(this.layoutSettings);
    }

    get settingsHasLayers() {
        return InstanceUtils.hasLayers(this.layoutSettings);
    }

    dispose() {
        this.disposeReactions();
    }
}
